import { useModelComparison } from '../../hooks/useAmiAnalytics.ts'

interface Props {
  feederId: number
}

const FLAG_STYLES: Record<string, string> = {
  ok: 'bg-green-100 text-green-700',
  disagrees: 'bg-red-100 text-red-700',
  not_comparable: 'bg-gray-100 text-gray-500',
}

const fmtSigned = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(4)}`

export function ModelComparisonPanel({ feederId }: Props) {
  const { data, isLoading, error } = useModelComparison(feederId)

  if (isLoading) return <div className="text-xs text-gray-400 p-3">Comparing model vs measured…</div>
  if (error) return <div className="text-xs text-red-500 p-3">Failed to load comparison.</div>
  if (!data) return null

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-700">
          Model vs Measured — {data.feeder_name ?? `Feeder ${data.feeder_id}`}
        </h3>
        <div className="text-[11px] text-gray-500">
          window {data.window_days}d · PF {data.power_factor_used} ·
          {' '}{data.ok} ok / {data.disagrees} disagree / {data.not_comparable} n/a
        </div>
      </div>
      <p className="text-[11px] text-gray-400 mb-2">{data.caveat}</p>
      {data.median_deviation_pu != null && (
        <p className="text-[11px] text-amber-600 mb-2">
          Systematic offset: {fmtSigned(data.median_deviation_pu)} pu (median Δ
          across comparable transformers) — expected where the model anchors the
          source at 1.0 pu but the substation regulates above nominal. The
          corrected column removes it so per-transformer outliers stand out.
        </p>
      )}
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-white">
            <tr>
              {['Transformer', 'Avg kW (measured)', 'Modeled pu', 'Measured pu',
                'Δ pu', 'Δ pu (corr.)', 'Coverage L/V', 'Flag'].map(h => (
                <th key={h} className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.transformers.map(t => (
              <tr key={t.transformer_id} className="hover:bg-gray-50">
                <td className="px-2 py-1.5">{t.name ?? t.transformer_id}</td>
                <td className="px-2 py-1.5">{t.measured_avg_kw ?? '—'}</td>
                <td className="px-2 py-1.5">
                  {t.modeled_voltage_pu != null ? t.modeled_voltage_pu.toFixed(4) : '—'}
                </td>
                <td className="px-2 py-1.5">
                  {t.measured_avg_pu != null ? t.measured_avg_pu.toFixed(4) : '—'}
                </td>
                <td className="px-2 py-1.5 font-medium">
                  {t.deviation_pu != null ? t.deviation_pu.toFixed(4) : '—'}
                </td>
                <td className="px-2 py-1.5 text-gray-600">
                  {t.offset_corrected_deviation_pu != null
                    ? fmtSigned(t.offset_corrected_deviation_pu)
                    : '—'}
                </td>
                <td className="px-2 py-1.5 text-gray-500">
                  {t.load_coverage_pct ?? '—'}% / {t.voltage_coverage_pct ?? '—'}%
                </td>
                <td className="px-2 py-1.5">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${FLAG_STYLES[t.flag] ?? ''}`}>
                    {t.flag === 'not_comparable'
                      ? `n/a: ${(t.not_comparable_reason ?? '').replace(/_/g, ' ')}`
                      : t.flag}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
